import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useSpring, useTransform } from 'framer-motion';
import { Menu, User, Settings, LogOut, RefreshCw, HelpCircle, Zap, Info, AlertTriangle } from 'lucide-react';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';
import { apiSlice } from '@/core/api/apiSlice';
import { useGetBillingBalanceQuery } from '../../api/billingApi';

interface AdminHeaderProps {
  adminUser: any;
  setSidebarOpen: (open: boolean) => void;
  onRefresh?: () => void;
  isLoading?: boolean;
}

function AnimatedNumber({ value }: { value: number }) {
  const spring = useSpring(value, { mass: 1, stiffness: 100, damping: 20 });
  const display = useTransform(spring, (current) => Math.round(current).toLocaleString());

  useEffect(() => {
    spring.set(value);
  }, [spring, value]);

  return <motion.span>{display}</motion.span>;
}

export function AdminHeader({
  adminUser,
  setSidebarOpen,
  onRefresh,
  isLoading,
}: AdminHeaderProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);

  // Animation States
  const [floatingCredits, setFloatingCredits] = useState<{ id: string; amount: number; targetX: number; targetY: number }[]>([]);
  const [badgePulse, setBadgePulse] = useState(false);
  const badgeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleSpendCredits = (e: any) => {
      const amount = e.detail?.amount || 5;
      
      // Calculate target coordinates based on badge position
      let targetX = window.innerWidth - 100;
      let targetY = 26; // Approx top-right
      
      if (badgeRef.current) {
        const rect = badgeRef.current.getBoundingClientRect();
        targetX = rect.left + rect.width / 2;
        targetY = rect.top + rect.height / 2;
      }

      const newAnim = {
        id: Math.random().toString(36).substr(2, 9),
        amount,
        targetX,
        targetY
      };

      setFloatingCredits(prev => [...prev, newAnim]);
    };

    window.addEventListener('spend-credits', handleSpendCredits);
    return () => window.removeEventListener('spend-credits', handleSpendCredits);
  }, []);

  const handleAnimationComplete = (id: string) => {
    setFloatingCredits(prev => prev.filter(anim => anim.id !== id));
    
    // Trigger pulse
    setBadgePulse(true);
    setTimeout(() => setBadgePulse(false), 500);
  };

  // Fetch billing balance if user is not a SuperAdmin
  const { data: balanceResponse } = useGetBillingBalanceQuery(undefined, {
    skip: !adminUser || adminUser.role === 'SuperAdmin',
  });
  const credits = balanceResponse?.data?.balance || 0;
  const isLowCredits = credits > 0 && credits <= 20;                                                                              
  const isEmptyCredits = credits === 0;

  const handleLogout = () => {
    Swal.fire({
      title: 'Sign Out',
      text: 'Are you sure you want to end your session?',
      icon: 'question',
      showCancelButton: true,
      customClass: { confirmButton: 'swal-danger' },
      confirmButtonText: 'Yes, sign out',
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        dispatch(apiSlice.util.resetApiState());
        navigate('/login');
      }
    });
  };

  // Build page label for breadcrumb
  const getPageLabel = () => {
    if (location.pathname.includes('/team')) return 'Team';
    if (location.pathname.includes('/knowledge')) return 'Knowledge Base';
    if (location.pathname.includes('/pre-call')) return 'Pre-Call Intel';
    if (location.pathname.includes('/copilot-settings')) return 'Shark Settings';
    if (location.pathname.includes('/integrations')) return 'Integrations';
    if (location.pathname.includes('/organizations')) return 'Organizations';
    if (location.pathname.includes('/profile')) return 'Profile';
    if (location.pathname.includes('/sessions')) return 'Session Analytics';
    return 'Dashboard';
  };

  const pageLabel = getPageLabel();

  return (
    <>
      <header className="admin-header flex items-center justify-between border-b" style={{ height: '52px', minHeight: '52px', padding: '0 24px' }}>
      {/* Left: Mobile menu + Breadcrumb */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setSidebarOpen(true)}
          className="md:hidden p-1 rounded transition-colors"
          style={{ color: 'var(--admin-card-subtext)' }}
        >
          <Menu size={18} />
        </button>
        {/* Breadcrumb */}
        <div className="hidden md:flex items-center gap-1" style={{ fontSize: '13px', color: 'var(--admin-card-subtext)', fontFamily: 'var(--admin-font-primary)', fontWeight: 700 }}>
          <span style={{ opacity: 0.6, fontSize: '12px', letterSpacing: '0.05em' }}>ADMIN CONSOLE</span>
          <span style={{ opacity: 0.35, margin: '0 8px', fontSize: '12px' }}>/</span>
          <span style={{ color: 'var(--admin-card-text)', fontWeight: 700, fontSize: '13px' }}>{pageLabel}</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {/* Refresh Button */}
        {onRefresh && (
          <button
            onClick={onRefresh}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '5px 12px',
              border: '1px solid var(--admin-card-border)',
              borderRadius: '0',
              fontSize: '11px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
            className="hover:opacity-80"
          >
            <RefreshCw size={11} className={isLoading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        )}

        {/* Credits Badge & Get Credits Button */}
        {adminUser?.plan && adminUser?.role !== 'SuperAdmin' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            
            {/* Premium Credits Badge with Tooltip */}
            <div className="relative group">
              <motion.div 
                ref={badgeRef}
                title="Available Credits"
                animate={
                  badgePulse 
                    ? { scale: [1, 1.1, 0.95, 1], borderColor: ['rgba(56, 189, 248, 0.4)', '#eab308', 'rgba(56, 189, 248, 0.4)'], color: ['#38bdf8', '#eab308', '#38bdf8'] } 
                    : isEmptyCredits
                      ? { boxShadow: ['0 0 5px rgba(239, 68, 68, 0.1)', '0 0 15px rgba(239, 68, 68, 0.4)', '0 0 5px rgba(239, 68, 68, 0.1)'] }
                      : isLowCredits
                        ? { boxShadow: ['0 0 5px rgba(245, 158, 11, 0.1)', '0 0 15px rgba(245, 158, 11, 0.4)', '0 0 5px rgba(245, 158, 11, 0.1)'] }
                        : {}
                }
                transition={
                  badgePulse
                    ? { duration: 0.5 }
                    : (isEmptyCredits || isLowCredits)
                      ? { duration: 2, repeat: Infinity, ease: "easeInOut" }
                      : {}
                }
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '4px 12px',
                  border: isEmptyCredits ? '1px solid rgba(239, 68, 68, 0.5)' : isLowCredits ? '1px solid rgba(245, 158, 11, 0.5)' : '1px solid rgba(56, 189, 248, 0.4)',
                  background: isEmptyCredits ? 'linear-gradient(90deg, rgba(239,68,68,0.15) 0%, rgba(239,68,68,0.02) 100%)' : isLowCredits ? 'linear-gradient(90deg, rgba(245,158,11,0.15) 0%, rgba(245,158,11,0.02) 100%)' : 'linear-gradient(90deg, rgba(56,189,248,0.15) 0%, rgba(56,189,248,0.02) 100%)',
                  borderRadius: '0',
                  fontSize: '11px',
                  fontWeight: 800,
                  letterSpacing: '0.05em',
                  fontFamily: 'Space Grotesk, sans-serif',
                  color: isEmptyCredits ? '#ef4444' : isLowCredits ? '#f59e0b' : 'var(--color-status-sky, #38bdf8)',
                  gap: '6px',
                  boxShadow: isEmptyCredits ? '0 0 10px rgba(239, 68, 68, 0.15)' : isLowCredits ? '0 0 10px rgba(245, 158, 11, 0.15)' : '0 0 10px rgba(56, 189, 248, 0.15)',
                  cursor: 'help'
              }}>
                {(isEmptyCredits || isLowCredits) ? <AlertTriangle size={12} fill="currentColor" /> : <Zap size={12} fill="currentColor" />}
                <span><AnimatedNumber value={credits} /> CR</span>
                <Info size={11} style={{ opacity: 0.7, marginLeft: '2px' }} />
              </motion.div>

              {/* Hover Tooltip Breakdown */}
              <div 
                className="absolute right-0 top-full mt-2 hidden group-hover:flex flex-col z-50 w-64"
                style={{
                   backgroundColor: 'var(--admin-bg-container, #010204)',
                   backgroundImage: 'radial-gradient(circle 120px at top right, rgba(56, 189, 248, 0.1) 0%, transparent 100%)',
                   border: '1px solid var(--admin-card-border)',
                   boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.4), 0 8px 10px -6px rgba(0, 0, 0, 0.4)',
                   padding: '16px',
                   borderRadius: '0',
                   color: 'var(--admin-card-text, #ffffff)',
                   fontFamily: "'Inter', sans-serif"
                }}
              >
                <h4 style={{ margin: '0 0 14px', fontSize: '11px', fontWeight: 800, color: 'var(--color-status-sky, #38bdf8)', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Space Grotesk, sans-serif' }}>
                  Credit Usage Guide
                </h4>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '10px' }}>
                  <span style={{ color: '#94a3b8' }}>Live Co-Pilot</span>
                  <span style={{ fontWeight: 600 }}>1 CR / min</span>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '10px' }}>
                  <span style={{ color: '#94a3b8' }}>Pre-Call (Recon)</span>
                  <span style={{ fontWeight: 600 }}>5 CR</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '14px' }}>
                  <span style={{ color: '#94a3b8' }}>Post-Call & CRM</span>
                  <span style={{ fontWeight: 600 }}>5 CR</span>
                </div>

                <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', fontSize: '12px', alignItems: 'center' }}>
                  <span style={{ color: '#e2e8f0', fontWeight: 600 }}>Avg. 30-min call</span>
                  <span style={{ color: 'var(--color-status-sky, #38bdf8)', fontWeight: 800, fontSize: '14px', fontFamily: 'Space Grotesk, sans-serif' }}>
                    45 CR
                  </span>
                </div>
              </div>
            </div>

            {adminUser.plan !== 'enterprise' && adminUser.role !== 'member' && (
              <button
                onClick={() => navigate('/admin/billing')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '4px 12px',
                  border: '1px solid var(--admin-sidebar-logo-text, #ffffff)',
                  backgroundColor: 'var(--admin-sidebar-logo-text, #ffffff)',
                  color: 'var(--admin-bg-container, #010204)',
                  borderRadius: '0',
                  fontSize: '10px',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  fontFamily: 'Space Grotesk, sans-serif',
                }}
                className="hover:opacity-90"
              >
                Get Credits
              </button>
            )}
          </div>
        )}

        {/* Help / Tour Button */}
        {adminUser?.role !== 'SuperAdmin' && (
          <button
            onClick={() => window.dispatchEvent(new Event('start-finn-tour'))}
            title="Restart Tour Guide"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: '1px solid var(--admin-card-border)',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
            }}
            className="hover:opacity-80"
          >
            <HelpCircle size={15} />
          </button>
        )}

        {/* Profile Avatar + Dropdown */}
        <div className="relative">
          <button
            onClick={() => setProfileOpen(!profileOpen)}
            className="admin-profile-btn flex items-center justify-center rounded-full transition-all duration-200 font-bold cursor-pointer"
            style={{ width: '32px', height: '32px', fontSize: '13px' }}
          >
            {adminUser?.name ? adminUser.name.charAt(0).toUpperCase() : 'A'}
          </button>

          <AnimatePresence>
            {profileOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.12 }}
                  className="admin-dropdown"
                >
                  <div className="admin-dropdown-header" style={{ borderBottom: '1px solid var(--admin-card-border)', paddingBottom: '12px', marginBottom: '8px' }}>
                    <p className="admin-dropdown-header-label" style={{ color: 'var(--admin-card-subtext)', fontSize: '10px', fontWeight: 600, letterSpacing: '0.05em' }}>SIGNED IN AS</p>
                    <p className="admin-dropdown-header-email" style={{ color: 'var(--admin-card-text)', fontSize: '13px', fontWeight: 500, marginTop: '2px' }}>{adminUser?.email || 'admin@finnai.com'}</p>
                  </div>

                  <button
                    onClick={() => { setProfileOpen(false); navigate('/admin/profile'); }}
                    className="admin-dropdown-item"
                    style={{ color: 'var(--admin-card-text)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <div className="flex items-center gap-2">
                      <User size={13} />
                      <span>Profile</span>
                    </div>
                  </button>

                  <button className="admin-dropdown-item disabled" disabled style={{ color: 'var(--admin-card-subtext)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: 0.7 }}>
                    <div className="flex items-center gap-2">
                      <Settings size={13} />
                      <span>Settings</span>
                    </div>
                    <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 6px', background: 'var(--color-glass-surface, rgba(255,255,255,0.05))', color: 'var(--admin-card-subtext)', border: '1px solid var(--admin-card-border)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Soon
                    </span>
                  </button>

                  <div style={{ height: '1px', background: 'var(--admin-card-border)', margin: '8px 0' }} />

                  <button
                    onClick={() => { setProfileOpen(false); handleLogout(); }}
                    className="admin-dropdown-item logout"
                    style={{ color: '#EF4444', display: 'flex', alignItems: 'center', gap: '2px' }}
                  >
                    <div className="flex items-center gap-2">
                      <LogOut size={13} />
                      <span className="font-semibold">Sign Out</span>
                    </div>
                  </button>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>

    </header>

      {/* Floating Credit Animations */}
      {floatingCredits.map(anim => (
        <motion.div
          key={anim.id}
          initial={{ 
            opacity: 0, 
            scale: 0.5, 
            x: window.innerWidth / 2, 
            y: window.innerHeight / 2,
            rotate: -10
          }}
          animate={{ 
            opacity: [0, 1, 1, 0],
            scale: [0.5, 1.5, 1.2, 0.4],
            x: anim.targetX - 20, // Offset to center on badge
            y: anim.targetY - 20,
            rotate: [0, 10, -5, 0]
          }}
          transition={{ 
            duration: 1.2, 
            ease: "easeInOut",
            times: [0, 0.2, 0.7, 1]
          }}
          onAnimationComplete={() => handleAnimationComplete(anim.id)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 9999,
            pointerEvents: 'none',
            color: '#eab308', // Gold color for spending credits
            fontWeight: 900,
            fontSize: '28px',
            fontFamily: 'Space Grotesk, sans-serif',
            textShadow: '0 0 20px rgba(234, 179, 8, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px'
          }}
        >
          -{anim.amount} <Zap size={24} fill="currentColor" />
        </motion.div>
      ))}
    </>
  );
}
